import React, { useMemo, useEffect, useRef } from "react";
import styled from "@emotion/styled";
import { Eta as mmm } from "eta";
const Eta: any = mmm; // Import Eta template engine

// Initialize Eta template engine
const eta = new Eta({
  tags: ["{{", "}}"],
  useWith: true,
  autoEscape: false,
});

const BlockContainer = styled.div`
  position: relative;
  width: 100%;

  /* Container for CSS injection */
  .ui-block-styles {
    display: none;
  }
`;

const BlockContent = styled.div`
  width: 100%;
  position: relative;
`;

export interface UIBlockData {
  id: string;
  name: string;
  html: string;
  css: string;
  js?: string | null;
  schema: {
    editableFields: Record<string, string>;
  };
  slots?: Array<{
    id: string;
    name: string;
    displayName: string;
    isRepeatable: boolean;
    minItems: number;
    maxItems: number | null;
    allowedChildren: string[];
  }>;
  isContainer: boolean;
}

export interface UIBlockProps {
  block: UIBlockData;
  overrides?: Record<string, any>;
  slotData?: Record<string, any>;
  children?: React.ReactNode;
  onEdit?: () => void;
  onSlotEdit?: (slotName: string) => void;
  isEditable?: boolean;
  theme?: {
    colors: {
      primary: string;
      secondary: string;
      accent: string;
      background: string;
      text: string;
      border: string;
    };
    fonts: {
      heading: string;
      body: string;
    };
    spacing: {
      padding: string;
      margin: string;
      borderRadius: string;
    };
  };
}

const defaultTheme = {
  colors: {
    primary: "#3b82f6",
    secondary: "#1f2937",
    accent: "#f59e0b",
    background: "#ffffff",
    text: "#374151",
    border: "#e5e7eb",
  },
  fonts: {
    heading:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    body: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  spacing: {
    padding: "2rem",
    margin: "1rem",
    borderRadius: "8px",
  },
};

export const UIBlockRenderer: React.FC<UIBlockProps> = ({
  block,
  overrides = {},
  slotData = {},
  children,
  onEdit,
  onSlotEdit,
  isEditable = false,
  theme = defaultTheme,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const styleRef = useRef<HTMLStyleElement | null>(null);

  // Prepare template data combining default values with overrides
  const templateData = useMemo(() => {
    const defaultValues: Record<string, any> = {};

    // Extract default values from schema
    Object.entries(block.schema.editableFields || {}).forEach(
      ([key, defaultValue]) => {
        defaultValues[key] = defaultValue;
      }
    );

    // Merge with overrides, giving priority to overrides
    const mergedData = {
      ...defaultValues,
      ...overrides,
      ...theme,
    };

    console.log('UIBlockRenderer templateData debug:', {
      blockName: block.name,
      schemaFields: block.schema.editableFields,
      defaultValues,
      overrides,
      mergedData,
      theme
    });

    // Ensure all template variables have fallback values
    return new Proxy(mergedData, {
      get(target: any, prop: string | symbol) {
        // Handle symbol properties directly
        if (typeof prop === 'symbol') {
          return target[prop];
        }
        
        const key = prop as string;
        if (key in target) {
          console.log(`Template accessing ${key}:`, target[key]);
          return target[key];
        }
        // Return empty string for undefined properties to prevent template errors
        console.log(`Template accessing undefined ${key}, returning empty string`);
        return '';
      }
    });
  }, [block.schema.editableFields, overrides, theme]);

  // Render HTML using Eta template engine
  const renderedHtml = useMemo(() => {
    try {
      console.log('Rendering HTML for block:', block.name);
      console.log('Block HTML:', block.html);
      console.log('Template data keys:', Object.keys(templateData));
      const result = (eta as any).renderString(block.html, templateData);
      console.log('Rendered HTML result:', result);
      return result;
    } catch (error) {
      console.error("Error rendering HTML template:", error);
      console.error("Block:", block.name);
      console.error("HTML:", block.html);
      console.error("Template data:", templateData);
      return `<div style="color: red; padding: 1rem; border: 1px solid red;">
        Error rendering block: ${block.name}
        <br />
        ${error instanceof Error ? error.message : "Unknown error"}
      </div>`;
    }
  }, [block.html, templateData]);

  // Process slots in HTML for container blocks
  const processedHtml = useMemo(() => {
    console.log('Processing slots for block:', block.name, {
      isContainer: block.isContainer,
      slots: block.slots,
      slotsLength: block.slots?.length,
      slotData,
      children,
      isEditable,
      renderedHtml
    });

    if (!block.isContainer || !block.slots || block.slots.length === 0) {
      return renderedHtml;
    }

    let processed = renderedHtml;

    // Replace slot placeholders with actual content or edit buttons
    block.slots.forEach((slot) => {
      console.log('Processing slot:', slot.name, {
        isRepeatable: slot.isRepeatable,
        minItems: slot.minItems,
        maxItems: slot.maxItems,
        allowedChildren: slot.allowedChildren,
        slotDataForThisSlot: slotData[slot.name]
      });

      const slotPattern = new RegExp(`(<[^>]*data-slot="${slot.name}"[^>]*>)([^<]*<!--[^>]*-->)?`, "g");
      
      if (isEditable) {
        const slotReplacement = `<div data-slot="${slot.name}" style="min-height: 100px; border: 2px dashed #e5e7eb; padding: 1rem; text-align: center; cursor: pointer;" onclick="handleSlotEdit('${slot.name}')">Click to edit ${slot.displayName}</div>`;
        console.log('Replacing slot pattern:', slotPattern, 'with:', slotReplacement);
        processed = processed.replace(slotPattern, slotReplacement);
      } else {
        // Render actual slot content
        const slotItems = slotData[slot.name]?.items || [];
        console.log('Slot items for', slot.name, ':', slotItems);
        
        processed = processed.replace(slotPattern, (match: any, openingTag: any, content: any) => {
          // Add data-react-slot attribute to the existing element instead of creating a wrapper
          const modifiedTag = openingTag.replace('>', ` data-react-slot="${slot.name}">`);
          return `${modifiedTag}`;
        });
      }
    });

    console.log('Final processed HTML:', processed);
    return processed;
  }, [renderedHtml, block.isContainer, block.slots, slotData, isEditable, children]);

  // Render CSS using Eta template engine
  const renderedCss = useMemo(() => {
    try {
      return (eta as any).renderString(block.css, templateData);
    } catch (error) {
      console.error("Error rendering CSS template:", error);
      return `/* Error rendering CSS for block: ${block.name} */`;
    }
  }, [block.css, templateData]);

  // Inject CSS into the document head
  useEffect(() => {
    if (renderedCss) {
      // Create unique style element for this block
      const styleId = `ui-block-${block.id}-${Date.now()}`;

      // Remove existing style if it exists
      if (styleRef.current) {
        styleRef.current.remove();
      }

      // Create new style element
      const style = document.createElement("style");
      style.id = styleId;
      style.textContent = renderedCss;
      document.head.appendChild(style);
      styleRef.current = style;

      // Cleanup function
      return () => {
        if (styleRef.current) {
          styleRef.current.remove();
          styleRef.current = null;
        }
      };
    }
  }, [renderedCss, block.id]);

  // Execute JavaScript if provided
  useEffect(() => {
    if (block.js && containerRef.current) {
      try {
        // Create a function scope for the JS execution
        const executeJs = new Function(
          "container",
          "block",
          "templateData",
          block.js
        );
        executeJs(containerRef.current, block, templateData);
      } catch (error) {
        console.error("Error executing block JavaScript:", error);
      }
    }
  }, [block.js, templateData]);

  // Handle slot editing
  const handleSlotEdit = (slotName: string) => {
    if (onSlotEdit) {
      onSlotEdit(slotName);
    }
  };

  // Make handleSlotEdit available globally for onclick handlers
  useEffect(() => {
    if (isEditable) {
      (window as any).handleSlotEdit = handleSlotEdit;
    }
  }, [isEditable, handleSlotEdit]);

  // Render slot content as React components
  const renderSlotContent = () => {
    if (!block.isContainer || !block.slots || !slotData) {
      return null;
    }

    return block.slots.map((slot) => {
      const slotItems = slotData[slot.name]?.items || [];
      if (slotItems.length === 0) return null;

      return (
        <div key={slot.name} data-slot-content={slot.name} style={{ display: 'contents' }}>
          {slotItems.map((item: any) => {
            if (item.type === 'text') {
              return (
                <div key={item.id} className="slot-text-item">
                  {item.content}
                </div>
              );
            } else if (item.type === 'block' && item.blockData) {
              return (
                <UIBlockRenderer
                  key={item.id}
                  block={item.blockData}
                  overrides={item.overrides || {}}
                  theme={theme}
                  isEditable={false}
                />
              );
            }
            return null;
          })}
        </div>
      );
    });
  };

  // Process HTML to inject slot placeholders (already handled in processedHtml)
  const processHtmlWithSlots = () => {
    return processedHtml;
  };

  // Use effect to inject slot content into DOM using React portals
  React.useEffect(() => {
    if (block.isContainer && block.slots && slotData && containerRef.current) {
      const { createRoot } = require('react-dom/client');
      
      block.slots.forEach((slot) => {
        const slotItems = slotData[slot.name]?.items || [];
        const target = containerRef.current?.querySelector(`[data-react-slot="${slot.name}"]`);
        
        if (target && slotItems.length > 0) {
          // Create React elements for this slot
          const slotElements = slotItems.map((item: any) => {
            if (item.type === 'text') {
              return React.createElement('div', {
                key: item.id,
                className: 'slot-text-item'
              }, item.content);
            } else if (item.type === 'block' && item.blockData) {
              return React.createElement(UIBlockRenderer, {
                key: item.id,
                block: item.blockData,
                overrides: item.overrides || {},
                theme: theme,
                isEditable: false
              });
            }
            return null;
          }).filter(Boolean);

          // Render into the target element
          if (slotElements.length > 0) {
            const root = createRoot(target);
            root.render(React.createElement(React.Fragment, {}, ...slotElements));
          }
        }
      });
    }
  }, [block, slotData, theme, processedHtml]);

  return (
    <BlockContainer ref={containerRef}>
      {isEditable && (
        <div
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            zIndex: 10,
            background: "rgba(0, 0, 0, 0.7)",
            color: "white",
            padding: "4px 8px",
            fontSize: "12px",
            cursor: "pointer",
            borderRadius: "0 0 0 4px",
          }}
          onClick={onEdit}
        >
          Edit {block.name}
        </div>
      )}

      <BlockContent dangerouslySetInnerHTML={{ __html: processHtmlWithSlots() }} />

      {children && <div>{children}</div>}
    </BlockContainer>
  );
};

export default UIBlockRenderer;
